import type {
  ArtifactRecord,
  ProjectSummary,
  SessionSnapshot,
} from '../../../web/src/api';
import type {
  AgentStatus,
  CadenceAgent,
  CadenceArtifact,
  CadencePublishedArtifactContent,
  CadencePublishedArtifactDeliveryState,
  CadenceBioAdapter,
  CadencePublishedArtifactFormat,
  CadencePublishedArtifactPresentation,
  CadencePublishedHtmlContent,
  CadenceProject,
  CadenceResidue,
} from './types';

const agentBlueprints: Array<{
  id: string;
  name: string;
  role: string;
  accent: string;
  finding: string;
}> = [
  {
    id: 'literature-agent',
    name: '文献研究员',
    role: 'Literature Analyst',
    accent: 'var(--cadence-data-blue)',
    finding: 'ABCB1 介导的外排增强与模型 B 的耐药表型一致。',
  },
  {
    id: 'genomics-agent',
    name: '基因组分析员',
    role: 'Genomics Analyst',
    accent: 'var(--cadence-data-green)',
    finding: 'RNA-seq 差异表达支持 ABCB1 上调，但仍需独立队列验证。',
  },
  {
    id: 'molecular-agent',
    name: '分子机制研究员',
    role: 'Mechanism Analyst',
    accent: 'var(--cadence-brand)',
    finding: '优先检查 L557 周围的疏水口袋与底物通道。',
  },
  {
    id: 'protein-agent',
    name: '蛋白结构研究员',
    role: 'Protein Scientist',
    accent: 'var(--cadence-data-ochre)',
    finding: 'L557 的构象变化可能改变结合入口的可及性。',
  },
  {
    id: 'data-agent',
    name: '数据分析员',
    role: 'Data Analyst',
    accent: 'var(--cadence-data-violet)',
    finding: '三个实验批次的方向一致，批次效应尚未完全排除。',
  },
  {
    id: 'experiment-agent',
    name: '实验设计员',
    role: 'Experimental Designer',
    accent: 'var(--cadence-data-rose)',
    finding: '建议先用竞争性抑制实验区分外排与靶点亲和力机制。',
  },
  {
    id: 'verification-agent',
    name: '验证研究员',
    role: 'Verification Scientist',
    accent: 'var(--cadence-data-blue)',
    finding: '当前结论仍是关联性证据，不能表述为因果确认。',
  },
  {
    id: 'safety-agent',
    name: '实验安全员',
    role: 'Lab Safety Reviewer',
    accent: 'var(--cadence-data-green)',
    finding: '后续远程计算任务需要先确认数据出境和资源权限。',
  },
  {
    id: 'clinical-agent',
    name: '转化研究员',
    role: 'Translational Analyst',
    accent: 'var(--cadence-brand)',
    finding: '耐药标志物与临床响应的关联需要更大样本支持。',
  },
  {
    id: 'assay-agent',
    name: '检测方法研究员',
    role: 'Assay Scientist',
    accent: 'var(--cadence-data-ochre)',
    finding: '优先锁定可复现的流式检测读数作为下一步输入。',
  },
  {
    id: 'review-agent',
    name: '同行审阅员',
    role: 'Scientific Reviewer',
    accent: 'var(--cadence-data-violet)',
    finding: '结果摘要已具备审阅结构，但来源链仍需补齐。',
  },
  {
    id: 'runtime-agent',
    name: '运行环境管理员',
    role: 'Runtime Steward',
    accent: 'var(--cadence-data-rose)',
    finding: 'Notebook 执行环境可复现，外部网络保持关闭。',
  },
];

const demoResidues: CadenceResidue[] = [
  { position: 552, aminoAcid: 'G', note: '入口环' },
  { position: 553, aminoAcid: 'F' },
  { position: 554, aminoAcid: 'L' },
  { position: 555, aminoAcid: 'A' },
  { position: 556, aminoAcid: 'V' },
  { position: 557, aminoAcid: 'L', note: '候选结合位点' },
  { position: 558, aminoAcid: 'S' },
  { position: 559, aminoAcid: 'Y' },
  { position: 560, aminoAcid: 'I' },
  { position: 561, aminoAcid: 'G' },
  { position: 562, aminoAcid: 'D' },
  { position: 563, aminoAcid: 'R' },
];

export const demoProject: CadenceProject = {
  id: 'adc-03',
  name: 'ADC-03 机制研究项目',
  subtitle: '为什么 ADC-03 在模型 B 中疗效显著下降？',
  question: '为什么 ADC-03 在模型 B 中疗效显著下降？',
  status: 'running',
  owner: '张研 究员',
  updatedAt: '2 分钟前',
};

export const demoArtifacts: CadenceArtifact[] = [
  {
    id: 'rna-seq-notebook',
    name: 'RNA-seq 差异分析.ipynb',
    type: 'Notebook',
    description: '批次校正、差异表达与 ABCB1 关联分析。',
    updatedAt: '2 分钟前',
    size: '2.4 MB',
    owner: 'genomics-agent',
    versions: [
      { id: 'v3', label: 'Version 3', createdAt: '今天 10:24', note: '加入批次效应敏感性分析' },
      { id: 'v2', label: 'Version 2', createdAt: '今天 09:50' },
    ],
    provenance: [
      { source: 'Genomics Agent · Python sandbox', recordedAt: '今天 10:24', evidenceId: 'ev-rna' },
      { source: 'Execution exec-24f6', recordedAt: '今天 10:24' },
    ],
    preview: { kind: 'text', content: '# RNA-seq 差异分析\n\nABCB1 在模型 B 中上调 4.7 倍。' },
  },
  {
    id: 'protein-structure',
    path: 'demo/ABCB1.pdb',
    kind: 'pdb',
    mimeType: 'chemical/x-pdb',
    name: 'ABCB1 结构查看器',
    type: 'Structure',
    description: '候选结合口袋与 L557 残基的原生结构投影。',
    updatedAt: '5 分钟前',
    size: '18.2 MB',
    owner: 'protein-agent',
    versions: [{ id: 'v1', label: 'Version 1', createdAt: '今天 10:18' }],
    provenance: [
      { source: 'Protein Agent · Structure renderer', recordedAt: '今天 10:18', evidenceId: 'ev-pocket' },
    ],
    preview: {
      kind: 'structure',
      content:
        'HEADER    CADENCE BIO DEMO STRUCTURE\nATOM      1  CA  LEU A 557      10.000  10.000  10.000  1.00 40.00           C\nATOM      2  CB  LEU A 557      11.200  10.300  10.200  1.00 40.00           C\nATOM      3  N   LEU A 557       9.200   9.200   9.600  1.00 40.00           N\nATOM      4  C   LEU A 557      10.500  11.000   9.100  1.00 40.00           C\nEND',
    },
  },
  {
    id: 'evidence-table',
    name: 'Flow 结果汇总.csv',
    type: 'CSV',
    description: '三批次流式实验的耐药表型汇总。',
    updatedAt: '12 分钟前',
    size: '164 KB',
    owner: 'data-agent',
    versions: [{ id: 'v4', label: 'Version 4', createdAt: '今天 10:12' }],
    provenance: [{ source: 'Data Agent · CSV export', recordedAt: '今天 10:12' }],
    preview: { kind: 'table', content: '模型 A,8732,71%\n模型 B,3106,6%' },
  },
  {
    id: 'mechanism-report',
    name: '耐药机制综述.md',
    type: 'Report',
    description: '当前证据、反例和待验证假设的研究报告。',
    updatedAt: '16 分钟前',
    size: '36 KB',
    owner: 'literature-agent',
    versions: [{ id: 'v6', label: 'Version 6', createdAt: '今天 10:05', note: '补充反例和证据等级' }],
    provenance: [
      { source: 'Literature Agent · review synthesis', recordedAt: '今天 10:05', evidenceId: 'ev-literature' },
    ],
    preview: { kind: 'text', content: '# 耐药机制综述\n\n当前结论为高可信关联，仍需因果验证。' },
  },
  {
    id: 'analysis-html',
    name: 'ABCB1 分析摘要.html',
    type: 'HTML',
    description: '可交互的单页分析摘要，展示当前候选机制。',
    updatedAt: '21 分钟前',
    size: '92 KB',
    owner: 'data-agent',
    versions: [{ id: 'v2', label: 'Version 2', createdAt: '今天 09:54' }],
    provenance: [{ source: 'Data Agent · HTML renderer', recordedAt: '今天 09:54' }],
    preview: {
      kind: 'html',
      content: `<!doctype html><html><body style="font-family: sans-serif; background:#1d1d1d; color:#f1f1f1; padding:24px"><h2>ABCB1 signal review</h2><p>Model B shows a 4.7x increase in the current projection.</p><button onclick="this.textContent = 'Selection captured'">Capture selection</button></body></html>`,
    },
  },
];

export function demoAgents(count = 4): CadenceAgent[] {
  return agentBlueprints.slice(0, Math.max(0, Math.min(agentBlueprints.length, count))).map((blueprint, index) => ({
    id: blueprint.id,
    name: blueprint.name,
    role: blueprint.role,
    accent: blueprint.accent,
    status: index === 5 ? 'waiting' : index === 6 ? 'paused' : index === 7 ? 'complete' : 'working',
    summary: index === 0
      ? '收集 ABCB1 与 ADC-03 耐药相关证据'
      : index === 1
        ? '整合 RNA-seq 与表型数据'
        : index === 2
          ? '分析跨膜通道与底物外排机制'
          : '核查候选位点和下一步实验',
    finding: blueprint.finding,
    progress: undefined,
  }));
}

export function demoAdapter(count = 4): CadenceBioAdapter {
  return {
    mode: 'demo',
    projects: [demoProject],
    activeProjectId: demoProject.id,
    agents: demoAgents(count),
    artifacts: demoArtifacts,
    residues: demoResidues,
  };
}

export function agentCountFromLocation(location: Location = window.location): number {
  const rawValue = new URLSearchParams(location.search).get('agents');
  if (rawValue === null) return 4;
  const value = Number(rawValue);
  return [0, 2, 4, 8, 10, 12].includes(value) ? value : 4;
}

export function projectAdapterFromSnapshot(
  snapshot: SessionSnapshot | undefined,
  projectRecords: ProjectSummary[],
  activeProjectId?: string,
  projectQuestionFallback?: string,
): CadenceBioAdapter {
  const raw = snapshot?.raw ?? {};
  const currentId = activeProjectId ?? projectRecords[0]?.projectId;
  const currentProject = projectRecords.find(
    (project) => project.projectId === currentId,
  );
  const sessionTitle = snapshot?.session?.title?.trim();
  const earliestTurnQuestion = snapshot?.turns
    .map((turn) => turn.input.trim())
    .find((input) => input.length > 0);
  const liveQuestion =
    currentProject?.question?.trim() ||
    projectQuestionFallback?.trim() ||
    earliestTurnQuestion ||
    (sessionTitle && sessionTitle !== "新会话" ? sessionTitle : undefined);
  const projects: CadenceProject[] = projectRecords.map((project) => ({
    id: project.projectId,
    sessionId: project.sessionId,
    workspaceId: project.workspaceId,
    workspacePath: project.workspacePath,
    name: project.name,
    subtitle:
      project.projectId === currentId ? liveQuestion ?? project.name : project.name,
    question: project.projectId === currentId ? liveQuestion : undefined,
    status: projectStatus(project.status),
    owner: project.ownerUserId,
    ownerUserId: project.ownerUserId,
    resumable: project.resumable,
    updatedAt:
      typeof project.updatedAt === 'number'
        ? new Date(project.updatedAt).toLocaleString()
        : project.updatedAt,
  }));
  const project = projects.find((item) => item.id === currentId) ?? projects[0];
  const rawAgents = Array.isArray(raw.agents) ? raw.agents : [];
  const agents = rawAgents.flatMap((rawAgent, index) => {
    if (!rawAgent || typeof rawAgent !== 'object') return [];
    const value = rawAgent as Record<string, unknown>;
    const id = typeof value.id === 'string' ? value.id : `agent-${index + 1}`;
    return [{
      id,
      name: typeof value.name === 'string' ? value.name : id,
      role: typeof value.role === 'string' ? value.role : 'Agent',
      status: statusFromRaw(value.status),
      summary: typeof value.summary === 'string' ? value.summary : '正在工作',
      finding: typeof value.finding === 'string' ? value.finding : undefined,
      accent: agentBlueprints[index % agentBlueprints.length].accent,
    } satisfies CadenceAgent];
  });
  const rawResidues = Array.isArray(raw.residues) ? raw.residues : [];
  const residues: CadenceResidue[] = rawResidues.flatMap((rawResidue) => {
    if (!rawResidue || typeof rawResidue !== 'object') return [];
    const value = rawResidue as Record<string, unknown>;
    const position = Number(value.position);
    const aminoAcid = typeof value.amino_acid === 'string'
      ? value.amino_acid
      : typeof value.aminoAcid === 'string'
        ? value.aminoAcid
        : undefined;
    return Number.isInteger(position) && aminoAcid
      ? [{ position, aminoAcid, note: typeof value.note === 'string' ? value.note : undefined }]
      : [];
  });
  return {
    mode: 'live',
    projects,
    activeProjectId: project?.id,
    agents,
    artifacts: (snapshot?.artifacts ?? []).map(toCadenceArtifact),
    residues,
  };
}

export function toCadenceArtifact(artifact: ArtifactRecord): CadenceArtifact {
  const raw = artifact.raw;
  const provenance = artifactProvenanceRecord(artifact);
  const publishedPresentation = publishedArtifactPresentationFromRaw(raw);
  const explicitOwner =
    artifact.ownerAgentId ??
    (typeof raw.owner_agent_id === 'string'
      ? raw.owner_agent_id
      : typeof raw.ownerAgentId === 'string'
        ? raw.ownerAgentId
        : typeof raw.owner === 'string'
          ? raw.owner
          : null);
  const provenanceSource =
    artifact.provenanceSummary?.source ??
    stringProvenanceField(provenance, 'source');
  const provenanceLabel = [
    provenanceSource,
    artifact.provenanceSummary?.recordType,
    artifact.provenanceSummary?.tool,
  ]
    .filter((value): value is string => Boolean(value))
    .join(' · ');
  return {
    id: artifact.id,
    path: artifact.path,
    kind: artifact.kind,
    mimeType: artifact.mimeType,
    name: artifact.name,
    type: artifactType(artifact),
    description: undefined,
    updatedAt: '刚刚更新',
    size: artifact.size ? `${Math.round(artifact.size / 1024)} KB` : undefined,
    status: artifact.status,
    owner: explicitOwner,
    executionId: artifact.executionId,
    representation: stringProvenanceField(provenance, 'representation'),
    coordinateSource: stringProvenanceField(provenance, 'coordinate_source'),
    contentSha256: artifact.contentSha256,
    coordinateSha256: artifact.coordinateSha256,
    provenance: provenanceLabel
      ? [{
          source: provenanceLabel,
          evidenceId: artifact.executionId,
        }]
      : undefined,
    publishedPresentation,
    preview: undefined,
  };
}

const PUBLISHED_ARTIFACT_FORMATS = new Set<CadencePublishedArtifactFormat>([
  'document',
  'table',
  'figure',
  'html',
  'code',
  'decision',
]);

const PUBLISHED_ARTIFACT_DELIVERY_STATES = new Set<CadencePublishedArtifactDeliveryState>([
  'final',
  'draft',
  'blocked',
]);

/**
 * The server emits format at the Artifact boundary and the supporting display
 * fields under provenance.publication. Do not infer this from the body.
 */
export function publishedArtifactPresentationFromRaw(
  raw: unknown,
): CadencePublishedArtifactPresentation | undefined {
  const record = recordFromUnknown(raw);
  const provenance = recordFromUnknown(record?.provenance);
  const format = publishedArtifactFormat(record?.format ?? provenance?.format);
  if (!format) return undefined;

  const publication = recordFromUnknown(provenance?.publication);
  return {
    format,
    deliveryState: publishedArtifactDeliveryState(publication?.delivery_state),
    title: nonBlankString(publication?.title),
    summary: nonBlankString(publication?.summary),
    template: nonBlankString(publication?.template),
    citations: publicationCitationLabels(publication?.citations),
    confidence: stringOrNumber(publication?.confidence),
    supersedesArtifactId:
      nonBlankString(publication?.supersedes) ??
      nonBlankString(publication?.supersedes_artifact_id),
    caption: nonBlankString(publication?.caption),
    method: nonBlankString(publication?.method),
    dataSource: nonBlankString(publication?.data_source),
  };
}

function publishedArtifactDeliveryState(
  value: unknown,
): CadencePublishedArtifactDeliveryState | undefined {
  return typeof value === 'string' &&
    PUBLISHED_ARTIFACT_DELIVERY_STATES.has(value as CadencePublishedArtifactDeliveryState)
    ? value as CadencePublishedArtifactDeliveryState
    : undefined;
}

export function publishedArtifactContentFromRaw(
  raw: unknown,
  expectedFormat?: CadencePublishedArtifactFormat,
): CadencePublishedArtifactContent | undefined {
  const record = recordFromUnknown(raw);
  if (
    record?.cadence_publication_version !== 1 ||
    !publishedArtifactFormat(record.format)
  ) {
    return undefined;
  }
  const format = publishedArtifactFormat(record.format)!;
  if (expectedFormat && expectedFormat !== format) return undefined;
  const content = recordFromUnknown(record.content);
  if (!content) return undefined;
  switch (format) {
    case "table": {
      const columns = stringArray(content.columns);
      const rows = Array.isArray(content.rows)
        ? content.rows.filter(isScalarTableRow)
        : undefined;
      if (!columns?.length || !rows || rows.some((row) => !sameColumnSet(row, columns))) {
        return undefined;
      }
      return {
        format,
        columns,
        rows,
        notes: nonBlankString(content.notes),
      };
    }
    case "code": {
      const files = Array.isArray(content.files)
        ? content.files.flatMap((file) => {
            const record = recordFromUnknown(file);
            const path = nonBlankString(record?.path);
            const text = typeof record?.text === "string" ? record.text : undefined;
            return path !== undefined && text !== undefined ? [{ path, text }] : [];
          })
        : undefined;
      const howToRun = nonBlankString(content.how_to_run);
      const outputs = stringArray(content.outputs);
      if (!files?.length || files.length !== (content.files as unknown[]).length || !howToRun || !outputs) {
        return undefined;
      }
      return { format, files, howToRun, outputs };
    }
    case "decision": {
      const question = nonBlankString(content.question);
      const recommendation = nonBlankString(content.recommendation);
      const criteria = Array.isArray(content.criteria)
        ? content.criteria.flatMap((criterion) => {
            const record = recordFromUnknown(criterion);
            const name = nonBlankString(record?.name);
            const met = record?.met;
            const note = nonBlankString(record?.note);
            return name && typeof met === "boolean"
              ? [{ name, met, ...(note ? { note } : {}) }]
              : [];
          })
        : undefined;
      const basedOnArtifactIds = stringArray(content.based_on_artifact_ids);
      const risks = stringArray(content.risks);
      const nextActions = stringArray(content.next_actions);
      if (
        !question ||
        !recommendation ||
        !isDecisionRecommendation(recommendation) ||
        !criteria?.length ||
        criteria.length !== (content.criteria as unknown[]).length ||
        !basedOnArtifactIds ||
        !risks ||
        !nextActions
      ) {
        return undefined;
      }
      return {
        format,
        question,
        recommendation,
        criteria,
        basedOnArtifactIds,
        risks,
        nextActions,
      };
    }
    case "html": {
      // Do not normalize this string: the server may include a CSP wrapper.
      const html = content.html;
      return typeof html === "string" ? { format, html } : undefined;
    }
    case "document":
    case "figure":
      return undefined;
  }
}

/**
 * Artifact detail is the direct file body. Parse a supported raw JSON table or
 * decision only after the server-attested list record selected its format; the
 * body itself is never treated as a Cadence envelope.
 */
export function publishedArtifactContentFromDirectFile(
  detailContent: string | undefined,
  expectedFormat?: CadencePublishedArtifactFormat,
): CadencePublishedArtifactContent | undefined {
  if (typeof detailContent !== "string" || !expectedFormat) return undefined;
  if (expectedFormat === "html") {
    return publishedHtmlContentFromDetail(detailContent, expectedFormat);
  }
  if (expectedFormat !== "table" && expectedFormat !== "decision") {
    return undefined;
  }
  let value: unknown;
  try {
    value = JSON.parse(detailContent);
  } catch {
    return undefined;
  }
  if (expectedFormat === "table") {
    const record = recordFromUnknown(value);
    const columns = stringArray(record?.columns);
    const rawRows = Array.isArray(record?.rows) ? record.rows : undefined;
    const rows = rawRows
      ? rawRows.filter(isScalarTableRow)
      : undefined;
    if (
      columns?.length &&
      rows &&
      rows.length === rawRows?.length &&
      rows.every((row) => sameColumnSet(row, columns))
    ) {
      return {
        format: "table",
        columns,
        rows,
        notes: nonBlankString(record?.notes),
      };
    }
    if (!Array.isArray(value) || !value.length) return undefined;
    const directRows = value.filter(isScalarTableRow);
    const directColumns = Object.keys(directRows[0] ?? {});
    if (
      directRows.length !== value.length ||
      !directColumns.length ||
      !directRows.every((row) => sameColumnSet(row, directColumns))
    ) {
      return undefined;
    }
    return { format: "table", columns: directColumns, rows: directRows };
  }

  const content = recordFromUnknown(value);
  const question = nonBlankString(content?.question);
  const recommendation = nonBlankString(content?.recommendation);
  const criteria = Array.isArray(content?.criteria)
    ? content.criteria.flatMap((criterion) => {
        const record = recordFromUnknown(criterion);
        const name = nonBlankString(record?.name);
        const met = record?.met;
        const note = nonBlankString(record?.note);
        return name && typeof met === "boolean"
          ? [{ name, met, ...(note ? { note } : {}) }]
          : [];
      })
    : undefined;
  const basedOnArtifactIds = stringArray(content?.based_on_artifact_ids);
  const risks = stringArray(content?.risks);
  const nextActions = stringArray(content?.next_actions);
  if (
    !question ||
    !recommendation ||
    !isDecisionRecommendation(recommendation) ||
    !criteria?.length ||
    criteria.length !== (content?.criteria as unknown[]).length ||
    !basedOnArtifactIds ||
    !risks ||
    !nextActions
  ) {
    return undefined;
  }
  return {
    format: "decision",
    question,
    recommendation,
    criteria,
    basedOnArtifactIds,
    risks,
    nextActions,
  };
}

/**
 * Published HTML is served by the Artifact content endpoint as raw text/html.
 * The server-attested list/detail format remains the only authorization to
 * present those bytes as HTML.
 */
export function publishedHtmlContentFromDetail(
  detailContent: string | undefined,
  expectedFormat?: CadencePublishedArtifactFormat,
): CadencePublishedHtmlContent | undefined {
  return expectedFormat === "html" && typeof detailContent === "string"
    ? { format: "html", html: detailContent }
    : undefined;
}

function publishedArtifactFormat(
  value: unknown,
): CadencePublishedArtifactFormat | undefined {
  return typeof value === 'string' &&
    PUBLISHED_ARTIFACT_FORMATS.has(value as CadencePublishedArtifactFormat)
    ? value as CadencePublishedArtifactFormat
    : undefined;
}

function recordFromUnknown(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function nonBlankString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function stringOrNumber(value: unknown): string | number | undefined {
  return typeof value === 'string' && value.trim()
    ? value.trim()
    : typeof value === 'number' && Number.isFinite(value)
      ? value
      : undefined;
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const values = value.flatMap((item) => {
    const text = nonBlankString(item);
    return text ? [text] : [];
  });
  return values.length === value.length ? values : undefined;
}

function isScalarTableRow(
  value: unknown,
): value is Record<string, string | number | boolean | null> {
  const record = recordFromUnknown(value);
  return Boolean(
    record &&
      Object.values(record).every(
        (cell) =>
          cell === null ||
          typeof cell === "string" ||
          typeof cell === "boolean" ||
          (typeof cell === "number" && Number.isFinite(cell)),
      ),
  );
}

function sameColumnSet(
  row: Record<string, unknown>,
  columns: string[],
): boolean {
  const keys = Object.keys(row);
  return keys.length === columns.length && columns.every((column) => column in row);
}

function isDecisionRecommendation(
  value: string,
): value is "go" | "no_go" | "conditional_go" | "need_more_data" {
  return ["go", "no_go", "conditional_go", "need_more_data"].includes(value);
}

function publicationCitationLabels(value: unknown): string[] | undefined {
  const items = Array.isArray(value) ? value : value === undefined ? [] : [value];
  const labels = items.flatMap((item) => {
    const direct = nonBlankString(item);
    if (direct) return [direct];
    const record = recordFromUnknown(item);
    const label = record && [
      record.label,
      record.title,
      record.citation,
      record.reference,
      record.url,
    ].map(nonBlankString).find(Boolean);
    return label ? [label] : [];
  });
  return labels.length ? labels : undefined;
}

const imageExtensions = new Set([
  ".apng",
  ".avif",
  ".bmp",
  ".gif",
  ".jpeg",
  ".jpg",
  ".png",
  ".svg",
  ".tif",
  ".tiff",
  ".webp",
]);

const structureExtensions = new Set([".cif", ".ent", ".mmcif", ".pdb"]);

const structureMimeTypes = new Set([
  "application/cif",
  "application/mmcif",
  "application/x-cif",
  "application/x-mmcif",
  "application/x-pdb",
  "chemical/cif",
  "chemical/mmcif",
  "chemical/pdb",
  "chemical/x-cif",
  "chemical/x-mmcif",
  "chemical/x-pdb",
]);

export function isConceptualIllustrationArtifact(
  artifact: Pick<CadenceArtifact, "kind" | "representation">,
): boolean {
  return [artifact.kind, artifact.representation].some(
    (value) =>
      value?.trim().toLowerCase().replace(/_/g, "-") ===
      "conceptual-illustration",
  );
}

export function isImageArtifact(
  artifact: Pick<
    CadenceArtifact,
    "kind" | "representation" | "mimeType" | "name" | "path"
  >,
): boolean {
  if (isConceptualIllustrationArtifact(artifact)) return true;
  const mimeType = artifact.mimeType?.split(";", 1)[0].trim().toLowerCase();
  if (mimeType?.startsWith("image/")) return true;
  return artifactFileNames(artifact).some((name) =>
    imageExtensions.has(fileExtension(name)),
  );
}

export function isCoordinateStructureArtifact(
  artifact: Pick<
    CadenceArtifact,
    | "kind"
    | "representation"
    | "coordinateSource"
    | "mimeType"
    | "name"
    | "path"
  >,
): boolean {
  if (
    isImageArtifact(artifact) ||
    artifact.coordinateSource?.trim().toLowerCase() === "none"
  ) {
    return false;
  }
  const kind = artifact.kind?.trim().toLowerCase();
  const mimeType = artifact.mimeType?.split(";", 1)[0].trim().toLowerCase();
  return (
    kind === "pdb" ||
    kind === "cif" ||
    kind === "mmcif" ||
    (mimeType !== undefined && structureMimeTypes.has(mimeType)) ||
    artifactFileNames(artifact).some((name) =>
      structureExtensions.has(fileExtension(name)),
    )
  );
}

function artifactType(artifact: ArtifactRecord): CadenceArtifact['type'] {
  if (isImageArtifact(artifact)) return 'Image';
  const kind = artifact.kind?.trim().toLowerCase();
  const mimeType = artifact.mimeType?.split(';', 1)[0].trim().toLowerCase();
  const names = artifactFileNames(artifact);
  const coordinateSource = stringProvenanceField(
    artifactProvenanceRecord(artifact),
    'coordinate_source',
  );
  if (kind === 'notebook' || names.some((name) => name.endsWith('.ipynb')))
    return 'Notebook';
  if (
    kind === 'csv' ||
    kind === 'table' ||
    mimeType === 'text/csv' ||
    mimeType === 'application/csv' ||
    names.some((name) => name.endsWith('.csv'))
  ) {
    return 'CSV';
  }
  if (isCoordinateStructureArtifact({ ...artifact, coordinateSource })) {
    return 'Structure';
  }
  if (kind === 'html' || mimeType === 'text/html' || names.some((name) => name.endsWith('.html')))
    return 'HTML';
  if (
    kind === 'report' ||
    names.some((name) => /\.(md|pdf|docx)$/u.test(name))
  ) {
    return 'Report';
  }
  return 'Data';
}

function artifactFileNames(
  artifact: Pick<CadenceArtifact, "name" | "path">,
): string[] {
  return [artifact.path, artifact.name]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value.trim().toLowerCase().split(/[?#]/u, 1)[0]);
}

function fileExtension(value: string): string {
  const slash = value.lastIndexOf("/");
  const dot = value.lastIndexOf(".");
  return dot > slash ? value.slice(dot) : "";
}

function artifactProvenanceRecord(
  artifact: ArtifactRecord,
): Record<string, unknown> | undefined {
  if (
    artifact.provenance &&
    typeof artifact.provenance === 'object' &&
    !Array.isArray(artifact.provenance)
  ) {
    return artifact.provenance;
  }
  const provenance = artifact.raw.provenance;
  return provenance &&
    typeof provenance === 'object' &&
    !Array.isArray(provenance)
    ? provenance as Record<string, unknown>
    : undefined;
}

function stringProvenanceField(
  provenance: Record<string, unknown> | undefined,
  key: string,
): string | undefined {
  const value = provenance?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function statusFromRaw(value: unknown): AgentStatus {
  if (typeof value !== 'string') return 'unknown';
  const status = value.toLowerCase();
  if (status.includes('error') || status.includes('fail')) return 'error';
  if (status.includes('complete') || status.includes('done') || status.includes('shutdown')) return 'complete';
  if (status.includes('pause')) return 'paused';
  if (status.includes('work') || status.includes('run')) return 'working';
  if (status.includes('wait') || status.includes('idle')) return 'waiting';
  return 'unknown';
}

function projectStatus(value: string): CadenceProject['status'] {
  const status = value.toLowerCase();
  if (status.includes('archive')) return 'archived';
  if (status.includes('complete') || status.includes('done')) return 'complete';
  if (status.includes('pause') || status.includes('saved')) return 'paused';
  return 'running';
}
